/**
 * PromptTestPanel — dry-run the editor's current body against ONE real
 * trajectory. Nothing is written: no label, no branch, no PR.
 *
 * The verbatim reply is shown above the parsed result on purpose. A reply
 * that broke the output contract is the one worth reading, and it is exactly
 * the case where there is nothing parsed to show.
 *
 * `readOnly` disables the run: the dry run is a POST, which the API refuses
 * under staff impersonation and for non-employees on a restricted demo
 * team, so offering it there only produces a 403.
 */

import { useState } from 'react'
import { AlertTriangle, Play } from 'lucide-react'

import type { PromptTestResponse } from '@/api/generated/model'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface PromptTestPanelProps {
  kind: string
  isRunning: boolean
  result: PromptTestResponse | null
  error: string | null
  /** Anchor handed over from another page (e.g. /review's "Fix prompt"). */
  initialAnchor?: string
  readOnly: boolean
  onRun: (anchor: { traceId: string }) => void
}

export function PromptTestPanel({
  kind,
  isRunning,
  result,
  error,
  initialAnchor,
  readOnly,
  onRun,
}: PromptTestPanelProps): React.JSX.Element {
  // Seeded once on mount, which is enough: /settings/prompts is only reached by
  // a route change, so arriving from "Fix prompt" always remounts this.
  const [anchor, setAnchor] = useState(initialAnchor ?? '')

  const inputId = `prompt-test-anchor-${kind}`

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-2">
        <div className="min-w-0 flex-1 space-y-1">
          <Label htmlFor={inputId}>Trace ID</Label>
          <Input
            id={inputId}
            value={anchor}
            placeholder="00000000-0000-0000-0000-000000000000"
            disabled={readOnly}
            onChange={(e) => { setAnchor(e.target.value) }}
          />
        </div>
        <Button
          disabled={readOnly || isRunning || anchor.trim() === ''}
          onClick={() => {
            onRun({ traceId: anchor.trim() })
          }}
        >
          <Play className="mr-1 h-3 w-3" />
          {isRunning ? 'Running…' : 'Run test'}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        The trace to judge. Copy the ?item= value from a /review URL, or use
        “Fix prompt” there. Uses the text in the editor above, saved or not,
        and writes nothing.
      </p>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Test failed</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {result && (
        <div className="space-y-4">
          <section className="space-y-1">
            <h4 className="text-sm font-medium">Model reply (verbatim)</h4>
            <pre className="max-h-64 overflow-auto rounded-md border bg-muted/40 p-3 text-xs whitespace-pre-wrap break-words">
              {result.raw_reply}
            </pre>
          </section>

          {result.parse_error ? (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Reply did not match the output contract</AlertTitle>
              <AlertDescription>
                {result.parse_error}
                <span className="mt-1 block">
                  A real run would log this and skip the trajectory.
                </span>
              </AlertDescription>
            </Alert>
          ) : (
            <Findings result={result} />
          )}

          <details className="rounded-md border p-3">
            <summary className="cursor-pointer text-sm font-medium">
              Prompt sent to the model
            </summary>
            <pre className="mt-2 max-h-64 overflow-auto text-xs whitespace-pre-wrap break-words text-muted-foreground">
              {result.rendered_prompt}
            </pre>
          </details>
        </div>
      )}
    </div>
  )
}

function Findings({ result }: { result: PromptTestResponse }): React.JSX.Element {
  if (result.turn_verdict !== null && result.turn_verdict !== undefined) {
    return (
      <section className="space-y-2">
        <h4 className="text-sm font-medium">Trajectory classification</h4>
        <Badge variant={result.turn_verdict.label === 'good' ? 'secondary' : 'destructive'}>
          {result.turn_verdict.label}
        </Badge>
        <p className="text-xs text-muted-foreground">This test does not send a callback.</p>
      </section>
    )
  }
  const findings = result.findings ?? []
  if (findings.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No findings. This trajectory would not have been flagged.
      </p>
    )
  }
  return (
    <section className="space-y-2">
      <h4 className="text-sm font-medium">
        {findings.length} finding(s). Each would become a review-queue item
      </h4>
      <ul className="space-y-2">
        {findings.map((finding, i) => (
          <li
            key={`${finding.lean}-${String(i)}`}
            className="flex gap-2 rounded-md border p-2 text-sm"
          >
            <Badge variant={finding.lean === 'good' ? 'secondary' : 'destructive'}>
              {finding.lean}
            </Badge>
            <span className="min-w-0 flex-1">{finding.reason}</span>
            <span className="shrink-0 text-xs text-muted-foreground">
              {finding.anchor_index === null || finding.anchor_index === undefined
                ? 'whole trajectory'
                : `request ${String(finding.anchor_index)}`}
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}
